import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, Check, Plus, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";

const fieldCls = "bg-slate-900 border-slate-700 text-slate-100 h-10";
const iconBtn = "text-slate-400 hover:text-white hover:bg-slate-800";

function GroupRow({ group, index, total, onRename, onMove, onDelete }) {
  const [name, setName] = useState(group.name);
  useEffect(() => setName(group.name), [group.name]);
  const dirty = name.trim() && name.trim() !== group.name;

  return (
    <li className="flex items-center gap-1.5 py-2" data-testid={`group-row-${group.id}`}>
      <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={60} className={fieldCls} data-testid={`group-name-input-${group.id}`} />
      <Button size="icon" variant="ghost" disabled={!dirty} onClick={() => onRename(group, name.trim())} title="Salva nome" className={iconBtn} data-testid={`group-rename-button-${group.id}`}><Check className="w-4 h-4" /></Button>
      <Button size="icon" variant="ghost" disabled={index === 0} onClick={() => onMove(index, -1)} title="Sposta su" className={iconBtn} data-testid={`group-up-button-${group.id}`}><ArrowUp className="w-4 h-4" /></Button>
      <Button size="icon" variant="ghost" disabled={index === total - 1} onClick={() => onMove(index, 1)} title="Sposta giù" className={iconBtn} data-testid={`group-down-button-${group.id}`}><ArrowDown className="w-4 h-4" /></Button>
      <Button size="icon" variant="ghost" onClick={() => onDelete(group)} title="Elimina" className="text-slate-400 hover:text-rose-400 hover:bg-slate-800" data-testid={`group-delete-button-${group.id}`}><Trash2 className="w-4 h-4" /></Button>
    </li>
  );
}

export const GroupsDialog = ({ open, onOpenChange, groups, setGroups, onGroupDeleted }) => {
  const [newName, setNewName] = useState("");

  const run = async (fn, msg) => {
    try {
      await fn();
      if (msg) toast.success(msg);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const add = (e) => {
    e.preventDefault();
    run(async () => {
      const { data } = await api.post("/groups", { name: newName });
      setGroups((g) => [...g, data]);
      setNewName("");
    }, "Sezione creata");
  };

  const rename = (g, name) => run(async () => {
    const { data } = await api.put(`/groups/${g.id}`, { name });
    setGroups((l) => l.map((x) => (x.id === g.id ? data : x)));
  }, "Sezione rinominata");

  const move = (i, dir) => run(async () => {
    const next = [...groups];
    [next[i], next[i + dir]] = [next[i + dir], next[i]];
    const { data } = await api.post("/groups/reorder", { ids: next.map((g) => g.id) });
    setGroups(data);
  });

  const remove = (g) => {
    if (!window.confirm(`Eliminare la sezione "${g.name}"? I siti al suo interno non verranno eliminati.`)) return;
    run(async () => {
      await api.delete(`/groups/${g.id}`);
      setGroups((l) => l.filter((x) => x.id !== g.id));
      onGroupDeleted(g.id);
    }, "Sezione eliminata");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} className="bg-[#111827] border-slate-700 text-slate-100 sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="groups-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Sezioni</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-400">Crea sezioni (es. Lavoro, Clienti) e assegna i siti dalla finestra di modifica di ogni riquadro.</p>
        <form onSubmit={add} className="flex gap-2">
          <Input required value={newName} onChange={(e) => setNewName(e.target.value)} maxLength={60} placeholder="Nome nuova sezione" className={fieldCls} data-testid="add-group-name-input" />
          <Button className="h-10 bg-blue-600 hover:bg-blue-700 text-white" data-testid="add-group-submit-button"><Plus className="w-4 h-4 mr-1" /> Crea</Button>
        </form>
        {groups.length === 0 ? (
          <p className="text-sm text-slate-500 py-2" data-testid="groups-empty">Nessuna sezione creata.</p>
        ) : (
          <ul className="divide-y divide-slate-800" data-testid="groups-list">
            {groups.map((g, i) => (
              <GroupRow key={g.id} group={g} index={i} total={groups.length} onRename={rename} onMove={move} onDelete={remove} />
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
};
